import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { trim } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';

const SearchOrderDialog = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const lastKeyStrokeTime = useRef(Date.now());
  const isScannerDetected = useRef(true);
  const autoSearchedValue = useRef('');

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSearch = useCallback(
    (isAuto = false) => {
      const term = trim(inputValue);

      if (!term) return;
      if (isAuto && term === autoSearchedValue.current) return;
      if (isAuto) autoSearchedValue.current = term;

      navigate(`/orders/${term}`);
      setInputValue('');
      handleClose();
    },
    [inputValue, navigate]
  );

  const onChange = (event) => {
    const newValue = event.target.value;
    const now = Date.now();
    const addedChars = newValue.length - inputValue.length;

    if (addedChars > 1 || newValue.length <= 1) {
      isScannerDetected.current = true;
    } else {
      const diff = now - lastKeyStrokeTime.current;
      if (diff > 50) {
        isScannerDetected.current = false;
      }
    }

    if (newValue !== autoSearchedValue.current) {
      autoSearchedValue.current = '';
    }

    lastKeyStrokeTime.current = now;
    setInputValue(newValue);
  };

  useEffect(() => {
    const term = trim(inputValue);

    if (isScannerDetected.current && term.length >= 5 && term !== autoSearchedValue.current) {
      const delayDebounceFn = setTimeout(() => {
        handleSearch(true);
      }, 150);

      return () => clearTimeout(delayDebounceFn);
    }

    return undefined;
  }, [inputValue, handleSearch]);

  useEffect(() => {
    if (!open) {
      setInputValue('');
      autoSearchedValue.current = '';
      isScannerDetected.current = true;
    }
  }, [open]);

  useHotkeys('ctrl + shift + f', handleOpen);

  return (
    <Dialog open={open} fullWidth maxWidth='sm'>
      <DialogTitle>Search Order</DialogTitle>

      <DialogContent>
        <TextField
          sx={{ mt: 3 }}
          label='Order Id'
          autoFocus
          onChange={onChange}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(false)}
          value={inputValue}
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText='Must be at least 5 characters'
        />
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={handleClose} sx={{ width: 75 }}>
          Close
        </Button>

        <LoadingButton
          variant='contained'
          onClick={() => handleSearch(false)}
          sx={{ width: 75 }}
          disabled={trim(inputValue).length < 5}
        >
          Search
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SearchOrderDialog;
